<template>
  <portal :to="'combined-stats-' + componentId">
    <div class="w-1/6 | table-left-cell">Display / Video</div>
    <div class="w-1/6 | table-cell numeric text-right">
      {{ budgetRemainingPerDay | money }}
    </div>
    <div class="w-1/6 | table-cell numeric text-right">
      {{ budgetRemaining | money }}
    </div>
    <div
      class="w-1/6 | table-cell numeric text-right"
      :class="budgetHighlight(percentBudgetOverUnder)"
    >
      {{ percentBudgetOverUnder | percent }}
    </div>
    <div
      class="w-1/6 | table-cell numeric text-right"
      :class="impressionsHighlight(percentImpressionsOverUnder)"
    >
      {{ percentImpressionsOverUnder | percent }}
    </div>
    <div
      class="w-1/6 | table-cell numeric text-right"
      :class="mediaHighlight(percentForMediaOverUnder)"
    >
      {{ percentForMediaOverUnder | percent }}
    </div>
  </portal>
</template>

<script>
import HighlightMixin from "./HighlightMixin"
import { each } from "lodash"

export default {
  name: "BudgetCPMCombinedItem",
  mixins: [HighlightMixin],
  props: {
    data: {
      required: true,
    },
    daysInPeriod: {
      required: true,
    },
    elapsedDaysInPeriod: {
      required: true,
    },
    componentId: {
      required: true,
    },
  },
  data() {
    return {
      higlights: {
        green: "green",
        yellow: "yellow",
        red: "red",
      },
      budgetKeys: {
        display: {
          google: "google_display",
          google_remarketing: "google_display_remarketing",
          facebook: "facebook_display",
          facebook_remarketing: "facebook_display_remarketing",
          simplifi: "simplifi_display",
        },
        video: {
          google: "google_video",
          google_remarketing: "google_video_remarketing",
          facebook: "facebook_video",
          facebook_remarketing: "facebook_video_remarketing",
          simplifi: "simplifi_tv",
          tremor: "tremor_tv",
        },
      },
      serviceNames: {
        google_display: "Google",
        google_display_remarketing: "Google RM",
        facebook_display: "Facebook",
        facebook_display_remarketing: "Facebook RM",
        google_video: "Google",
        google_video_remarketing: "Google RM",
        facebook_video: "Facebook",
        facebook_video_remarketing: "FB RM",
        simplifi_display: "Simpli.fi",
        simplifi_tv: "Simpli.fi",
        tremor_tv: "Tremor",
      },
    }
  },
  computed: {
    actualBudget() {
      let total = 0
      each(this.budgetKeys["display"], (item, key) => {
        total += this.data.stats["display"][key].cost
      })
      each(this.budgetKeys["video"], (item, key) => {
        total += this.data.stats["video"][key].cost
      })

      return total
    },
    actualImpressions() {
      let total = 0
      each(this.budgetKeys["display"], (item, key) => {
        total += this.data.stats["display"][key].impressions
      })
      each(this.budgetKeys["video"], (item, key) => {
        total += this.data.stats["video"][key].impressions
      })

      return total
    },
    targetBudget() {
      let total = 0
      each(this.budgetKeys["display"], (item) => {
        total +=
          this.data["display"].budgets[item] *
          (this.data["display"].percent / 100)
      })
      each(this.budgetKeys["video"], (item) => {
        total +=
          this.data["video"].budgets[item] * (this.data["video"].percent / 100)
      })

      return total
    },
    targetBudgetToDate() {
      return (this.targetBudget / this.daysInPeriod) * this.elapsedDaysInPeriod
    },
    budgetRemaining() {
      return this.targetBudget - this.actualBudget
    },
    budgetRemainingPerDay() {
      let divisor = this.daysInPeriod - this.elapsedDaysInPeriod

      return divisor > 0 ? this.budgetRemaining / divisor : this.budgetRemaining
    },
    percentBudgetOverUnder() {
      return this.targetBudgetToDate > 0
        ? this.actualBudget / this.targetBudgetToDate
        : 0
    },
    targetImpressions() {
      let total = 0
      each(this.budgetKeys["display"], (item) => {
        total +=
          (this.data["display"].budgets[item] / this.data["display"].cpm) * 1000
      })
      each(this.budgetKeys["video"], (item) => {
        total +=
          (this.data["video"].budgets[item] / this.data["video"].cpm) * 1000
      })

      return total
    },
    targetImpressionToDate() {
      return (
        (this.targetImpressions / this.daysInPeriod) * this.elapsedDaysInPeriod
      )
    },
    percentImpressionsOverUnder() {
      return this.targetImpressionToDate > 0
        ? this.actualImpressions / this.targetImpressionToDate
        : 0
    },
    percentForMediaOverUnder() {
      let cpm = (this.data["display"].cpm + this.data["video"].cpm) / 2

      return this.actualImpressions > 0 && cpm > 0
        ? this.actualBudget / ((this.actualImpressions / 1000) * cpm)
        : 0
    },
  },
  methods: {
    budgetHighlight(num) {
      return this.processHighlightItem(
        [
          {
            key: 1,
            operator: "gte",
            first: 85,
            second: null,
            color: "green",
          },
          {
            key: 2,
            operator: "between",
            first: 85,
            second: 70,
            color: "yellow",
          },
          {
            key: 3,
            operator: "lte",
            first: 70,
            second: null,
            color: "red",
          },
        ],
        num
      )
    },
    impressionsHighlight(num) {
      return this.processHighlightItem(
        [
          {
            key: 1,
            operator: "gte",
            first: 100,
            second: null,
            color: "green",
          },
          {
            key: 2,
            operator: "between",
            first: 100,
            second: 80,
            color: "yellow",
          },
          {
            key: 3,
            operator: "lte",
            first: 80,
            second: null,
            color: "red",
          },
        ],
        num
      )
    },
    mediaHighlight(num) {
      return this.processHighlightItem(
        [
          {
            key: 1,
            operator: "gte",
            first: 100,
            second: null,
            color: "red",
          },
          {
            key: 2,
            operator: "between",
            first: 100,
            second: 80,
            color: "yellow",
          },
          {
            key: 3,
            operator: "lte",
            first: 80,
            second: null,
            color: "green",
          },
        ],
        num
      )
    },
  },
}
</script>

<style lang="scss" scoped>
</style>
